import { Link } from "react-router";

import { CitySearchSelect } from "@/components/fields/city-search-select";
import { ListView } from "@/components/refine-ui/views/list-view";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AreasListTab } from "@/pages/areas/components/areas-list-tab";
import { AreasMapTab } from "@/pages/areas/components/areas-map-tab";
import { useAreaCities } from "@/pages/areas/hooks/use-area-explore";
import {
  AREAS_TABS,
  isAreasTab,
  useAreaPageState,
} from "@/pages/areas/hooks/use-area-page-state";
import { parseCityKey, cityKey as toCityKey } from "@/types/area";

/** The create page takes the list's city as the same `?city=&countryCode=` pair. */
const createQuery = (cityKey: string | undefined): string => {
  if (!cityKey) return "";
  const { city, countryCode } = parseCityKey(cityKey);
  return `?${new URLSearchParams({ city, countryCode })}`;
};

const LABELS = { list: "List", map: "Map" } as const;

/**
 * `/areas`: one page, two tabs, one city filter shared by both.
 *
 * The Issues tab that used to be the third moved on 2026-11-01 to the Area tab of
 * `/tina/audits`, where the nightly area step files the same issues as findings.
 */
export const AreasList = () => {
  const { tab, setTab, cityKey, setCityKey } = useAreaPageState();
  const { cities } = useAreaCities();
  const picked = cityKey ? parseCityKey(cityKey) : undefined;

  return (
    <ListView>
      <div className="flex items-center gap-3">
        <Tabs
          value={tab}
          onValueChange={(v) => {
            if (isAreasTab(v)) setTab(v);
          }}
        >
          <TabsList>
            {AREAS_TABS.map((t) => (
              <TabsTrigger key={t} value={t}>
                {LABELS[t]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <CitySearchSelect
          options={cities}
          city={picked?.city ?? null}
          country={picked?.countryCode ?? null}
          onChange={(pick) => setCityKey(pick ? toCityKey(pick) : undefined)}
        />
        <Button asChild size="sm" className="ml-auto">
          <Link to={`/areas/create${createQuery(cityKey)}`}>New area</Link>
        </Button>
      </div>
      {/* One tab mounted at a time: the List tab owns a `useTable`. */}
      {tab === "list" && <AreasListTab cityKey={cityKey} />}
      {tab === "map" && <AreasMapTab cityKey={cityKey} />}
    </ListView>
  );
};
